// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl } from './extensions.js';
/**
 * True iff `intakeType` is the builder-layer id for a yes/no field.
 * Pairs with {@link isFhirBooleanType} (the FHIR-layer counterpart).
 * Centralising both halves means a future rename of either layer
 * only touches this file.
 */
export function isYesNoFieldType(intakeType) {
    return intakeType === 'yesNo';
}
/**
 * True iff `fhirType` is the FHIR primitive a yes/no question
 * serialises to. Use this in code reasoning about the persisted
 * Questionnaire.item (storage / validation / EMR), where
 * {@link IntakeFormFieldType} is not in scope.
 */
export function isFhirBooleanType(fhirType) {
    return fhirType === 'boolean';
}
/** Look up the first extension on `host` with the given URL, if any. */
export function findExtension(host, url) {
    return host?.extension?.find((ext) => ext.url === url);
}
/** True iff `item.type === 'attachment'` and it carries the `signature` extension. */
export function isSignatureItem(item) {
    if (item.type !== 'attachment')
        return false;
    const ext = findExtension(item, ExtensionUrl.signature);
    return ext?.valueBoolean === true;
}
/**
 * Map a FHIR `Questionnaire.item` to one of intakeform's nine field types.
 * Returns `undefined` for FHIR item types we do not surface (`group`,
 * `display`, `dateTime`, etc.) so callers can decide whether to skip or
 * surface a builder error.
 */
export function itemToFieldType(item) {
    switch (item.type) {
        case 'string':
            return 'shortText';
        case 'text':
            return 'longText';
        case 'choice':
            return item.repeats === true ? 'multiSelect' : 'singleSelect';
        case 'decimal':
        case 'integer':
            return 'number';
        case 'date':
            return 'date';
        case 'boolean':
            return 'yesNo';
        case 'attachment':
            return isSignatureItem(item) ? 'signature' : 'fileUpload';
        default:
            return undefined;
    }
}
/** Inverse of {@link itemToFieldType}: the FHIR `item.type` for one of our field types. */
export function fieldTypeToItemType(fieldType) {
    switch (fieldType) {
        case 'shortText':
            return 'string';
        case 'longText':
            return 'text';
        case 'singleSelect':
        case 'multiSelect':
            return 'choice';
        case 'number':
            return 'decimal';
        case 'date':
            return 'date';
        case 'yesNo':
            return 'boolean';
        case 'signature':
        case 'fileUpload':
            return 'attachment';
    }
}
//# sourceMappingURL=conversions.js.map